import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = "May 11, 2026";

function Privacy() {
  return (
    <div className="bg-white min-h-screen">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-line">
        <div className="max-w-[1560px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-bold text-[15px] text-ink">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-xs font-extrabold">Q</span>
            <span>QuickBook Connector</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-muted px-3.5 py-2 border border-line rounded-lg hover:text-ink hover:bg-soft transition-all">
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-6 py-16 pb-24">
        <span className="inline-block text-xs font-semibold tracking-wider uppercase text-indigo mb-3.5">Legal</span>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-4 text-ink">Privacy Policy</h1>
        <div className="flex flex-wrap gap-3.5 items-center text-sm text-muted mb-9">
          <span className="px-3 py-1.5 border border-line rounded-full bg-soft">Last updated: {EFFECTIVE_DATE}</span>
          <span>Effective: {EFFECTIVE_DATE}</span>
        </div>

        <p className="text-[17px] text-muted p-5 bg-soft border-l-[3px] border-navy rounded-lg mb-12">
          This Privacy Policy describes how <strong>XcelerateAI Inc.</strong> ("we", "us", or "our") collects,
          uses, and safeguards information when you use <strong>QuickBook Connector</strong> (the "Service") —
          a developer middleware that connects applications to QuickBooks Online via OAuth 2.0.
        </p>

        <nav className="bg-soft border border-line rounded-xl p-6 mb-12" aria-label="Table of contents">
          <h4 className="m-0 mb-2.5 text-[13px] font-semibold tracking-wider uppercase text-muted">Contents</h4>
          <ol className="m-0 pl-5.5 text-ink text-[14.5px] space-y-1">
            <li><a href="#s1" className="text-ink hover:text-indigo hover:underline">Introduction</a></li>
            <li><a href="#s2" className="text-ink hover:text-indigo hover:underline">Information We Collect</a></li>
            <li><a href="#s3" className="text-ink hover:text-indigo hover:underline">How We Use Your Information</a></li>
            <li><a href="#s4" className="text-ink hover:text-indigo hover:underline">Data Storage & Security</a></li>
            <li><a href="#s5" className="text-ink hover:text-indigo hover:underline">Third-Party Services</a></li>
            <li><a href="#s6" className="text-ink hover:text-indigo hover:underline">Data Retention</a></li>
            <li><a href="#s7" className="text-ink hover:text-indigo hover:underline">Your Rights</a></li>
            <li><a href="#s8" className="text-ink hover:text-indigo hover:underline">Cookies & Sessions</a></li>
            <li><a href="#s9" className="text-ink hover:text-indigo hover:underline">Changes to This Policy</a></li>
            <li><a href="#s10" className="text-ink hover:text-indigo hover:underline">Contact Information</a></li>
          </ol>
        </nav>

        <h2 id="s1" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink first:border-t-0 first:pt-0 first:mt-0">1. Introduction</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          QuickBook Connector is a developer-focused, business-to-business tool that enables applications to
          authenticate with QuickBooks Online and perform accounting operations on behalf of authorized users.
          This policy applies to anyone who registers an account on a deployment of the Service or whose
          credentials and OAuth tokens are stored within it.
        </p>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          Because the Service is a backend component installed and operated by developers and businesses, the
          ultimate data controller may be the operator of a given deployment rather than XcelerateAI Inc.
          Where this policy refers to "we", it describes the default behavior of the software as published.
        </p>

        <h2 id="s2" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">2. Information We Collect</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">The Service collects only the information required to authenticate users and communicate with QuickBooks Online:</p>

        <h3 className="text-base font-semibold mt-6 mb-2 text-ink">Account information</h3>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>Email address</strong> — used as your unique identifier for sign-in.</li>
          <li><strong>Password</strong> — never stored in plaintext. Passwords are hashed with <code className="font-mono text-[13.5px] bg-soft border border-line px-1.5 py-0.5 rounded text-[#1a2240]">bcrypt</code> before being saved.</li>
        </ul>

        <h3 className="text-base font-semibold mt-6 mb-2 text-ink">QuickBooks OAuth tokens</h3>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>Access token</strong> and <strong>refresh token</strong> issued by Intuit, encrypted at rest.</li>
          <li>Token expiration timestamps required to refresh credentials automatically.</li>
        </ul>

        <h3 className="text-base font-semibold mt-6 mb-2 text-ink">QuickBooks company / organization data</h3>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>Company name</strong> and <strong>realm ID</strong> (the QuickBooks organization identifier) returned by Intuit when you connect.</li>
          <li>Additional company metadata fetched from QuickBooks only as needed to fulfill API requests you initiate.</li>
        </ul>

        <h3 className="text-base font-semibold mt-6 mb-2 text-ink">Session data</h3>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li>An HTTP-only session cookie named <code className="font-mono text-[13.5px] bg-soft border border-line px-1.5 py-0.5 rounded text-[#1a2240]">qb.sid</code>.</li>
          <li>Short-lived <strong>JWT tokens</strong> used to authorize API requests.</li>
        </ul>

        <h3 className="text-base font-semibold mt-6 mb-2 text-ink">Server logs</h3>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li>API request logs including endpoint paths, HTTP methods, timestamps, status codes, and the originating IP address.</li>
          <li>Logs are intended for security monitoring, debugging, and abuse prevention.</li>
        </ul>

        <h2 id="s3" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">3. How We Use Your Information</h2>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li>To <strong>authenticate users</strong> and maintain authorized sessions.</li>
          <li>To <strong>connect to and communicate with QuickBooks Online</strong> on your behalf via the Intuit API.</li>
          <li>To <strong>automatically refresh OAuth tokens</strong> before they expire so your integration stays connected.</li>
          <li>To <strong>provide access to QuickBooks accounting data</strong> through the Service's REST API endpoints.</li>
          <li>To monitor the security and reliability of the Service and to investigate misuse.</li>
        </ul>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">We do not sell personal information, and we do not use your data for advertising or profiling.</p>

        <h2 id="s4" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">4. Data Storage & Security</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">The Service applies industry-standard practices to protect the information it stores:</p>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>MongoDB</strong> is used as the persistent data store for accounts, tokens, and organization records.</li>
          <li><strong>bcrypt</strong> is used to hash all user passwords; plaintext passwords are never stored or logged.</li>
          <li><strong>OAuth tokens are encrypted at rest</strong> using a server-side secret before being written to the database.</li>
          <li><strong>HTTP-only cookies</strong> are used for session storage so client-side JavaScript cannot read them.</li>
          <li><strong>JWT tokens</strong> are signed with a server-side secret and validated on every authenticated request.</li>
          <li><strong>Environment variables</strong> protect secrets such as the Intuit client ID and secret, JWT signing key, and token-encryption key.</li>
        </ul>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          No system can be guaranteed to be 100% secure. Operators deploying the Service are responsible for
          enforcing transport security (HTTPS), keeping dependencies up to date, and protecting their environment
          variables and database backups.
        </p>

        <h2 id="s5" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">5. Third-Party Services</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">The Service relies on the following third party:</p>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li>
            <strong>QuickBooks Online / Intuit</strong> — the OAuth provider and the source of all accounting data
            accessed through the Service. Your use of QuickBooks data is governed by{" "}
            <a href="https://www.intuit.com/privacy/" target="_blank" rel="noreferrer" className="text-indigo underline underline-offset-2 hover:no-underline">Intuit's own privacy policy</a>{" "}
            and the QuickBooks Online terms of service.
          </li>
        </ul>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          The Service does not transmit personal data to any other third party in its default configuration.
        </p>

        <h2 id="s6" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">6. Data Retention</h2>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>Access tokens</strong> issued by Intuit expire approximately <strong>1 hour</strong> after issuance.</li>
          <li><strong>Refresh tokens</strong> issued by Intuit expire approximately <strong>100 days</strong> after their last use.</li>
          <li><strong>Account data</strong> (email, hashed password, connected organizations) is retained until you delete your account or the operator deletes it on your behalf.</li>
          <li><strong>Server logs</strong> are retained for the period configured by the operator (defaults vary by deployment).</li>
        </ul>

        <h2 id="s7" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">7. Your Rights</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">You may, at any time:</p>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>Disconnect QuickBooks</strong> from your account, which removes the stored OAuth tokens and revokes the Service's access to your QuickBooks organization.</li>
          <li><strong>Delete your account</strong>, which removes your email, password hash, organization records, and stored tokens from the database.</li>
          <li><strong>Request a copy</strong> of the personal data the Service holds about you by contacting the operator of your deployment.</li>
        </ul>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          Depending on your jurisdiction, you may have additional rights under laws such as the GDPR or CCPA.
          Contact the operator of your deployment to exercise those rights.
        </p>

        <h2 id="s8" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">8. Cookies & Sessions</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">The Service uses a single first-party session cookie:</p>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li><strong>Name:</strong> <code className="font-mono text-[13.5px] bg-soft border border-line px-1.5 py-0.5 rounded text-[#1a2240]">qb.sid</code></li>
          <li><strong>Purpose:</strong> Maintains your authenticated session between API requests.</li>
          <li><strong>Max age:</strong> 7 days</li>
          <li><strong>Flags:</strong> <code className="font-mono text-[13.5px] bg-soft border border-line px-1.5 py-0.5 rounded text-[#1a2240]">HttpOnly</code> (and <code className="font-mono text-[13.5px] bg-soft border border-line px-1.5 py-0.5 rounded text-[#1a2240]">Secure</code> when served over HTTPS)</li>
        </ul>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">The Service does not set advertising or analytics cookies.</p>

        <h2 id="s9" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">9. Changes to This Policy</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          We may update this Privacy Policy from time to time to reflect changes in the Service, applicable laws,
          or operational practices. When we do, we will revise the "Last updated" date at the top of this page.
          Significant changes will be communicated to operators of the Service through release notes.
        </p>

        <h2 id="s10" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">10. Contact Information</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">For questions or concerns about this Privacy Policy or our data practices, contact us at:</p>
        <div className="mt-8 p-6 border border-line rounded-xl bg-white">
          <p className="my-1.5 text-[15px]"><strong>XcelerateAI Inc.</strong></p>
          <p className="my-1.5 text-[15px]">Email: <a href="mailto:support@xcelerateai.com" className="text-indigo underline underline-offset-2 hover:no-underline">support@xcelerateai.com</a></p>
        </div>
      </main>

      <footer className="border-t border-line bg-soft">
        <div className="max-w-[1160px] mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-xs font-extrabold">Q</span>
                <span className="font-bold text-[15px] text-ink">QuickBook Connector</span>
              </div>
              <p className="text-sm text-muted leading-relaxed">
                Developer middleware for seamless QuickBooks Online integration via OAuth 2.0.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-ink mb-3">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-sm text-muted hover:text-indigo transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-muted hover:text-indigo transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-ink mb-3">Contact</h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:support@xcelerateai.com" className="text-sm text-muted hover:text-indigo transition-colors">
                    support@xcelerateai.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 border-t border-line">
            <p className="text-sm text-muted text-center">
              © {new Date().getFullYear()} XcelerateAI Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Privacy;
